import { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle2, Droplets, FileText, Home, Building2, Zap, Clock } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1666000346172-8e7e87547d2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBBdXN0cmFsaWFuJTIwaG91c2UlMjBleHRlcmlvcnxlbnwwfHx8fDE3ODIzMDM5NjR8MA&ixlib=rb-4.1.0&q=85";

const empty = {
  solicitor_name: "",
  solicitor_email: "",
  matter_number: "",
  purchaser_name: "",
  vendor_name: "",
  property_address: "",
  property_description: "",
  property_type: "",
  preferred_reading_date: "",
  settlement_date: "",
  service_type: "standard",
  notes: "",
};

export default function SolicitorRequest() {
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/jobs", form);
      setDone(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="grid lg:grid-cols-[40%_60%] min-h-[calc(100vh-4rem)]">
        {/* Left brand panel */}
        <div className="relative hidden lg:block">
          <img src={HERO_IMG} alt="Property" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/65" />
          <div className="relative h-full flex flex-col justify-between p-12 text-white sticky top-16">
            <div className="flex items-center gap-2 text-sky-300">
              <Droplets className="w-5 h-5" />
              <span className="text-xs tracking-[0.25em] uppercase font-semibold">Special Water Meter Reading</span>
            </div>
            <div>
              <h1 className="font-display text-4xl lg:text-5xl font-600 tracking-tight leading-none mb-5">
                Request a certified water meter reading for settlement.
              </h1>
              <p className="text-slate-200 text-base max-w-md leading-relaxed">
                Enter the matter details below. An accredited reader attends the property and returns a
                certificate confirming the matter, parties, property and the actual meter reading.
              </p>
              <div className="flex gap-8 mt-10">
                <div>
                  <div className="font-display text-3xl font-700 text-sky-300">$115</div>
                  <div className="text-xs tracking-wide text-slate-300 uppercase mt-1">Basic Fee</div>
                </div>
                <div>
                  <div className="font-display text-3xl font-700 text-sky-300">+$35</div>
                  <div className="text-xs tracking-wide text-slate-300 uppercase mt-1">Urgent</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex items-start justify-center p-6 sm:p-12 bg-slate-50">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-2xl"
          >
            {done ? (
              <div data-testid="job-success" className="bg-white border border-slate-200 rounded-xl p-8 text-center mt-8">
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-7 h-7 text-emerald-500" />
                </div>
                <h2 className="font-display text-2xl font-600 text-slate-900 mb-2">Reading request submitted</h2>
                <p className="text-slate-500 text-sm mb-1">
                  Matter <span className="font-medium text-slate-700">{done.matter_number}</span> —{" "}
                  {done.property_address}
                </p>
                <p className="text-slate-500 text-sm mb-6">Queued for an accredited meter reader.</p>
                <Button
                  data-testid="new-request-btn"
                  onClick={() => {
                    setForm(empty);
                    setDone(null);
                  }}
                  className="bg-sky-500 hover:bg-sky-600"
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <>
                <span className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-400">
                  Solicitor / Conveyancer
                </span>
                <h2 className="font-display text-3xl font-600 tracking-tight text-slate-900 mt-2 mb-1">
                  Special water meter reading request
                </h2>
                <p className="text-slate-500 text-sm mb-8">
                  No account needed — provide the matter details to be confirmed on the certificate.
                </p>

                <form onSubmit={submit} className="space-y-6" data-testid="job-form">
                  {/* Solicitor */}
                  <Section icon={<FileText className="w-4 h-4" />} title="Your details & matter" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Solicitor name">
                      <Input data-testid="solicitor-name-input" required value={form.solicitor_name} onChange={update("solicitor_name")} placeholder="Full name" />
                    </Field>
                    <Field label="Solicitor email">
                      <Input data-testid="solicitor-email-input" type="email" required value={form.solicitor_email} onChange={update("solicitor_email")} placeholder="name@example.com" />
                    </Field>
                  </div>
                  <Field label="Matter number">
                    <Input data-testid="matter-number-input" required value={form.matter_number} onChange={update("matter_number")} placeholder="e.g. 100234" />
                  </Field>

                  {/* Parties */}
                  <Section icon={<Home className="w-4 h-4" />} title="Parties" />
                  <Field label="Purchaser name">
                    <Input data-testid="purchaser-name-input" required value={form.purchaser_name} onChange={update("purchaser_name")} placeholder="Full name(s)" />
                  </Field>
                  <Field label="Vendor name">
                    <Input data-testid="vendor-name-input" required value={form.vendor_name} onChange={update("vendor_name")} placeholder="Full name(s)" />
                  </Field>

                  {/* Property */}
                  <Section icon={<Building2 className="w-4 h-4" />} title="Property" />
                  <Field label="Property address">
                    <Input data-testid="property-address-input" required value={form.property_address} onChange={update("property_address")} placeholder="Street address, suburb, state, postcode" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Property description">
                      <Input data-testid="property-description-input" value={form.property_description} onChange={update("property_description")} placeholder="e.g. House, Unit, Land" />
                    </Field>
                    <Field label="Property type">
                      <Input data-testid="property-type-input" value={form.property_type} onChange={update("property_type")} placeholder="e.g. Residential, Strata" />
                    </Field>
                  </div>

                  {/* Dates */}
                  <Section icon={<Clock className="w-4 h-4" />} title="Dates" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Preferred reading date">
                      <Input data-testid="preferred-date-input" type="date" value={form.preferred_reading_date} onChange={update("preferred_reading_date")} />
                    </Field>
                    <Field label="Settlement date">
                      <Input data-testid="settlement-date-input" type="date" value={form.settlement_date} onChange={update("settlement_date")} />
                    </Field>
                  </div>

                  <Field label="Service type">
                    <div className="grid grid-cols-2 gap-3">
                      <ServiceCard active={form.service_type === "standard"} onClick={() => setForm({ ...form, service_type: "standard" })} testid="service-standard" icon={<Clock className="w-4 h-4" />} title="Standard" sub="$115" />
                      <ServiceCard active={form.service_type === "urgent"} onClick={() => setForm({ ...form, service_type: "urgent" })} testid="service-urgent" icon={<Zap className="w-4 h-4" />} title="Urgent" sub="$115 + $35" />
                    </div>
                  </Field>

                  <Field label="Notes / special instructions (optional)">
                    <Textarea data-testid="notes-input" value={form.notes} onChange={update("notes")} placeholder="Access instructions, meter location, etc." rows={3} />
                  </Field>

                  {error && <p data-testid="job-error" className="text-sm text-red-500">{error}</p>}

                  <Button data-testid="submit-job-btn" type="submit" disabled={submitting} className="w-full bg-sky-500 hover:bg-sky-600 h-11 text-base transition-all hover:-translate-y-0.5">
                    {submitting ? "Submitting…" : "Submit reading request"}
                  </Button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function Section({ icon, title }) {
  return (
    <div className="flex items-center gap-2 text-slate-400">
      {icon}
      <span className="text-xs tracking-[0.15em] uppercase font-semibold">{title}</span>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      {children}
    </div>
  );
}

function ServiceCard({ active, onClick, icon, title, sub, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition-all ${
        active ? "border-sky-500 bg-sky-50 ring-1 ring-sky-500" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <span className={`${active ? "text-sky-600" : "text-slate-400"}`}>{icon}</span>
      <span className="font-medium text-slate-900 text-sm">{title}</span>
      <span className="text-xs text-slate-500">{sub}</span>
    </button>
  );
}
